import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type AuthFormData = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  termsAccepted?: boolean;
};

// Define the allowed role types
type AppRole = "client" | "therapist" | "admin";

export const authService = {
  async signIn(email: string, password: string, selectedRole: "client" | "therapist" | null) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      if (error.message === "Invalid login credentials") {
        throw new Error(
          "Invalid login credentials. Please check your email and password, or sign up if you don't have an account."
        );
      }
      if (error.message === "Email not confirmed") {
        throw new Error(
          "Please confirm your email address before logging in. Check your inbox for a verification link."
        );
      }
      throw error;
    }
    
    // Ensure the session is available
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error("Failed to establish session. Please try again.");
    }
    
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', data.user.id)
      .maybeSingle();
      
    if (roleError) throw roleError;
    
    if (roleData) {
      const userRole = roleData.role;
      
      if (selectedRole === "client" && userRole === "therapist") {
        await this.silentSignOut();
        throw new Error("Error: User is not registered as a Client.");
      }
      
      if (selectedRole === "therapist" && userRole === "client") {
        await this.silentSignOut();
        throw new Error("Error: User is not registered as a Therapist.");
      }
      
      return { user: data.user, userRole };
    }
    
    // Silent sign out if no role is found
    await this.silentSignOut();
    return { user: null, userRole: null };
  },
  
  async signUp(formData: AuthFormData, defaultToClient: boolean) {
    const { email, password, firstName, lastName, company, termsAccepted } = formData;
    
    if (!termsAccepted) {
      throw new Error("You must accept the Terms and Conditions to register.");
    }
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback',
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`,
            name: company || "" 
          }
        },
      });

      if (error) {
        if (error.message.includes("already registered") || error.message.includes("already exists")) {
          throw new Error(
            "This email is already registered. Please try logging in instead."
          );
        }
        throw error;
      }
      
      if (!data.user) {
        toast.success(
          "Sign up successful! Please check your email to verify your account."
        );
        return { user: null };
      }
      
      const connectionKey = `${Math.random().toString(36).substring(2, 6)}-${Math.random().toString(36).substring(2, 6)}-${Math.random().toString(36).substring(2, 6)}`;
      
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: data.user.id,
          company: company || null,
          first_name: firstName,
          last_name: lastName,
          email: email,
          connection_key: connectionKey,
          updated_at: new Date().toISOString(),
          terms_accepted: true,
          terms_accepted_at: new Date().toISOString()
        });
        
      if (profileError) {
        console.error("Error creating user profile:", profileError);
      }
      
      if (defaultToClient) {
        try {
          // Check if role already exists before trying to insert
          const { data: existingRole } = await supabase
            .from('user_roles')
            .select('*')
            .eq('user_id', data.user.id)
            .eq('role', 'client')
            .maybeSingle();
            
          if (!existingRole) {
            const { error: roleError } = await supabase
              .from('user_roles')
              .insert({
                user_id: data.user.id,
                role: 'client'
              });
              
            if (roleError && roleError.code !== '23505') { // Ignore unique constraint violations
              console.error("Error setting client role:", roleError);
              throw roleError;
            }
          }
        } catch (roleError) {
          console.error("Error checking or setting role:", roleError);
          // Continue despite role errors, as the profile is created
        }
      }
      
      return { user: data.user };
    } catch (error) {
      console.error("Error during sign up:", error);
      throw error;
    }
  },
  
  async setUserRole(userId: string, role: AppRole) {
    try {
      // Check if the role already exists
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .eq('role', role)
        .maybeSingle();
        
      // Only insert if the role doesn't already exist
      if (!existingRole) {
        const { error } = await supabase
          .from('user_roles')
          .insert({
            user_id: userId,
            role: role
          });
        
        if (error) {
          if (error.code === '23505') {
            // Duplicate key error, role already exists
            console.log("Role already exists for user:", userId);
            return true;
          }
          console.error("Error setting role:", error);
          throw error;
        }
      } else {
        console.log("Role already exists, no need to insert:", role);
      }
      
      return true;
    } catch (error) {
      console.error("Error setting user role:", error);
      throw error;
    }
  },

  async silentSignOut() {
    try {
      console.log("Starting silent logout cleanup...");
      
      // Save the redirect path before clearing localStorage
      const redirectPath = localStorage.getItem('redirectAfterLogin');
      console.log("Saved redirect path:", redirectPath);
      
      // Clean up all localStorage items without notifications
      const keysToRemove = [
        'userEmail',
        'userName',
        'userId',
        'clientsInfo',
        'clamalo_auth_token',
        'supabase-auth-token',
        'lastVisitedPage',
        'isAdmin'
      ];
      
      console.log("Clearing localStorage keys:", keysToRemove);
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove ${key} from localStorage:`, e);
        }
      });
      
      // Restore the redirect path if it existed
      if (redirectPath) {
        console.log("Restoring redirect path:", redirectPath);
        localStorage.setItem('redirectAfterLogin', redirectPath);
      }
      
      // Clear session cookies with explicit attributes
      console.log("Clearing session cookies...");
      const cookies = document.cookie.split(';');
      const cleanupTime = new Date(0).toUTCString();
      
      cookies.forEach(cookie => {
        const [name] = cookie.split('=');
        if (name) {
          const trimmedName = name.trim();
          // Set multiple cookie variations to ensure cleanup
          document.cookie = `${trimmedName}=; expires=${cleanupTime}; path=/;`;
          document.cookie = `${trimmedName}=; expires=${cleanupTime}; path=/; domain=${window.location.hostname};`;
          document.cookie = `${trimmedName}=; expires=${cleanupTime}; path=/; secure; SameSite=Strict;`;
        }
      });
      
      // Force clear session storage
      console.log("Clearing session storage...");
      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn("Failed to clear sessionStorage:", e);
      }
      
      console.log("Signing out from Supabase...");
      await supabase.auth.signOut();
      
      // Verify cleanup
      console.log("Verifying cleanup...");
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        console.warn("Session persisted after logout, forcing reload");
        window.location.reload();
      } else {
        console.log("Cleanup successful - no session remains");
      }
      
      return { success: true };
    } catch (error) {
      console.error("Error during silent logout:", error);
      // Even if there's an error, try to force a reload
      window.location.reload();
      return { success: false, error };
    }
  },

  async signOut() {
    try {
      console.log("Starting logout process...");
      
      // First, sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out from Supabase:", error);
        throw error;
      }
      
      // Then clear localStorage items
      const keysToRemove = [
        'userEmail',
        'userName',
        'userId',
        'clientsInfo',
        'clamalo_auth_token',
        'supabase-auth-token',
        'lastVisitedPage',
        'isAdmin',
        'adminEmail'
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove ${key} from localStorage:`, e);
        }
      });
      
      // Clear session storage
      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn("Failed to clear sessionStorage:", e);
      }
      
      return { success: true };
    } catch (error) {
      console.error("Error during logout:", error);
      throw error;
    }
  },

  async resetPassword(email: string) {
    try {
      console.log("Attempting to reset password for email:", email);
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      console.log("Supabase password reset response:", { data, error });

      if (error) {
        console.error("Supabase password reset error:", {
          name: error.name,
          message: error.message,
          status: error.status,
          code: error.code,
          details: error
        });

        // Handle specific error cases
        if (error.status === 500 && error.code === 'unexpected_failure') {
          console.error("Server error details:", error);
          throw new Error("Email service configuration error. Please check your Supabase project settings and ensure email service is properly configured.");
        }
        if (error.message.includes("Email not found")) {
          throw new Error("No account found with this email address.");
        }
        if (error.message.includes("Email service not configured")) {
          throw new Error("Email service is not configured. Please enable email service in Supabase dashboard.");
        }
        if (error.message.includes("rate limit")) {
          throw new Error("Too many attempts. Please try again later.");
        }
        
        throw error;
      }

      console.log("Password reset email sent successfully");
      return { success: true };
    } catch (error) {
      console.error("Error resetting password:", error);
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error("Failed to send password reset email. Please try again later.");
    }
  }
};
